import {useCallback} from "react";
import useAPIService from "../../../hooks/useAPIService";
import {readStoredDob} from "../../myCase/dobSession";
import {toLookupId} from "../../../utils/customerAuth";
import {
  forgetShared,
  getVideoCatalog,
  sharedGet,
} from "../utils/sharedRequests";

const withDobQuery = (path, dateOfBirth) => {
  if (!dateOfBirth) return path;
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}date_of_birth=${encodeURIComponent(dateOfBirth)}`;
};

// One path per contact + DOB, so every caller (sidebar, video pages, Account)
// shares the same in-flight /get-contact request via sharedGet.
const contactBase = (contactId) => `/get-contact/id/${toLookupId(contactId)}`;
// Drop any /get-contact answer kept for reuse after writing to the contact.
export const forgetContact = (contactId) =>
  forgetShared(contactBase(contactId));

const contactPath = (contactId, dateOfBirth) =>
  withDobQuery(contactBase(contactId), dateOfBirth || readStoredDob(contactId));

export const useApi = () => {
  const {apiFetch} = useAPIService();

  const fetchVideos = useCallback(async () => {
    try {
      const {result, data} = await getVideoCatalog(apiFetch);
      if (result === "success") {
        // Copy: the catalog response is shared with other callers.
        return [...data].sort((a, b) => a.id.localeCompare(b.id));
      }
    } catch (err) {
      console.error(err);
    }
    return [];
  }, [apiFetch]);

  const fetchActivity = useCallback(
    async (contactId, dateOfBirth) => {
      const path = contactPath(contactId, dateOfBirth);
      const {result, data} = await sharedGet(apiFetch, path);
      if (result !== "success") {
        throw new Error("Activity request was not successful");
      }
      // Empty object only on a successful response with no activity — never
      // on network/API failure (callers must not treat failure as "no data").
      return data[0]?.activity || {};
    },
    [apiFetch],
  );

  const fetchWatchHistory = useCallback(
    async (contactId, dateOfBirth) => {
      const path = contactPath(contactId, dateOfBirth);
      const {result, data} = await sharedGet(apiFetch, path);
      if (result !== "success") {
        throw new Error("Watch history request was not successful");
      }
      return data[0]?.watch_history || {};
    },
    [apiFetch],
  );

  // The whole contact row (DOB-scoped when a DOB is stored). reuseMs lets a
  // caller take an answer another caller just received (see sharedGet).
  const fetchContact = useCallback(
    async (contactId, dateOfBirth, {reuseMs = 0} = {}) => {
      const {result, data} = await sharedGet(
        apiFetch,
        contactPath(contactId, dateOfBirth),
        {reuseMs},
      );
      if (result !== "success") {
        throw new Error("Contact request was not successful");
      }
      return data?.[0] ?? null;
    },
    [apiFetch],
  );

  const updateActivityAndHistory = useCallback(
    async ({user, activity, watchHistory, keepalive, dateOfBirth}) => {
      try {
        const dob =
          dateOfBirth ||
          (user?.customer_id ? readStoredDob(user.customer_id) : null);
        const {result, data} = await apiFetch(`/update-contact/activity`, {
          method: "PATCH",
          keepalive: keepalive,
          body: JSON.stringify({
            customer_id_u: user.customer_id,
            customer_firstname_u:
              user.firstname || user.given_name || "Unknown",
            customer_lastname_u: user.lastname || user.family_name || "Unknown",
            activity_u: activity || {},
            watch_history_u: watchHistory || {},
            timestamp_u: new Date().toISOString(),
            ...(dob ? {date_of_birth: dob} : {}),
          }),
        });
        if (result === "success") {
          // The saved row differs from any answer kept for reuse.
          forgetContact(user.customer_id);
          return data;
        }
      } catch (err) {
        console.error(err);
      }
      return {};
    },
    [apiFetch],
  );

  return {
    fetchVideos,
    fetchActivity,
    fetchWatchHistory,
    fetchContact,
    updateActivityAndHistory,
  };
};
